import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { getAllPlans, createPlan, updatePlan, deletePlan } from '../../lib/firebaseService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

export default function AdminPlans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price_monthly: '',
    price_yearly: '',
    max_products: '999', // Default unlimited
    features: '',
    purchase_link: '',
    popular: false
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadPlans();
  }, [user, navigate]);

  const loadPlans = async () => {
    try {
      const plansData = await getAllPlans();
      setPlans(plansData);
    } catch (error) {
      console.error('Planlar yüklənə bilmədi:', error);
      toast.error('Planlar yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const planData = {
        name: formData.name,
        price_monthly: parseFloat(formData.price_monthly) || 0,
        price_yearly: parseFloat(formData.price_yearly) || 0,
  
        features: formData.features.split('\n').filter(f => f.trim()),
        purchase_link: formData.purchase_link || '',
        popular: formData.popular
      };

      if (editingPlan) {
        await updatePlan(editingPlan.id, planData);
        toast.success('Plan yeniləndi');
      } else {
        await createPlan(planData);
        toast.success('Plan əlavə edildi');
      }

      setDialogOpen(false);
      resetForm();
      loadPlans();
    } catch (error) {
      console.error('Plan yadda saxlanmadı:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || '',
      price_monthly: plan.price_monthly?.toString() || '0',
      price_yearly: plan.price_yearly?.toString() || '0',

      features: Array.isArray(plan.features) ? plan.features.join('\n') : '',
      purchase_link: plan.purchase_link || '',
      popular: plan.popular || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Planı silmək istədiyinizə əminsiniz?')) return;
    
    try {
      await deletePlan(planId);
      toast.success('Plan silindi');
      loadPlans();
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    }
  };

  const resetForm = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      price_monthly: '',
      price_yearly: '',
      max_products: '',
      features: '',
      purchase_link: '',
      popular: false
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-plans-page">
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Geri
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">Abunəlik Planları</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button onClick={openNewDialog} data-testid="add-plan-button">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Plan
          </Button>
        </div>

        {loading ? (
          <p>Yüklənir...</p>
        ) : plans.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-12 text-center">
            <p className="text-muted-foreground mb-4">Heç bir plan yoxdur</p>
            <Button onClick={openNewDialog}>İlk planı yarat</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl p-6 border-2 transition-all duration-300 hover:shadow-xl ${
                  plan.popular ? 'border-black scale-105' : 'border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="bg-black text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-3">
                    POPULYAR
                  </div>
                )}
                <h3 className="text-2xl font-bold text-black mb-2">{plan.name}</h3>
                
                <div className="mb-6">
                  {plan.price_monthly === 0 ? (
                    <span className="text-4xl font-bold text-black">Pulsuz</span>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-black">{plan.price_monthly}</span>
                      <span className="text-gray-600"> AZN/ay</span>
                    </>
                  )}
                  {plan.price_yearly > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      və ya {plan.price_yearly} AZN/il
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {Array.isArray(plan.features) && plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-600">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.purchase_link && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-800 font-medium mb-1">Satınalma linki:</p>
                    <a 
                      href={plan.purchase_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {plan.purchase_link.substring(0, 40)}...
                    </a>
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(plan)}
                    data-testid={`edit-plan-${plan.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Redaktə
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(plan.id)}
                    data-testid={`delete-plan-${plan.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Planı Redaktə Et' : 'Yeni Plan'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="plan-form">
            <div>
              <Label htmlFor="name">Plan Adı *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Məsələn: Başlanğıc, Professional"
                required
                data-testid="plan-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price_monthly">Aylıq Qiymət (AZN)</Label>
                <Input
                  id="price_monthly"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_monthly}
                  onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                  placeholder="0 = Pulsuz"
                  data-testid="price-monthly-input"
                />
              </div>
              <div>
                <Label htmlFor="price_yearly">İllik Qiymət (AZN)</Label>
                <Input
                  id="price_yearly"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_yearly}
                  onChange={(e) => setFormData({ ...formData, price_yearly: e.target.value })}
                  data-testid="price-yearly-input"
                />
              </div>
            </div>

            <div className="hidden">
              <Label htmlFor="max_products">Maksimum Məhsul Sayı</Label>
              <Input
                id="max_products"
                type="number"
                value={formData.max_products}
                onChange={(e) => setFormData({ ...formData, max_products: e.target.value })}
                data-testid="max-products-input"
              />
              <p className="text-xs text-gray-500 mt-1">Avtomatik limitsiz (999)</p>
            </div>

            <div>
              <Label htmlFor="features">Xüsusiyyətlər (hər sətirdə bir)</Label>
              <Textarea
                id="features"
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                rows={4}
                placeholder="WhatsApp sifarişlər&#10;Limitsiz ziyarətçi&#10;Analitika"
                data-testid="features-input"
              />
            </div>

            <div>
              <Label htmlFor="purchase_link">Satınalma Linki</Label>
              <Input
                id="purchase_link"
                type="url"
                value={formData.purchase_link}
                onChange={(e) => setFormData({ ...formData, purchase_link: e.target.value })}
                placeholder="https://..."
                data-testid="purchase-link-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                İstifadəçi "İndi al" basdıqda bu linkə yönləndirilir
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="popular"
                checked={formData.popular}
                onChange={(e) => setFormData({ ...formData, popular: e.target.checked })}
                className="w-4 h-4"
                data-testid="popular-checkbox"
              />
              <Label htmlFor="popular">Populyar Plan kimi göstər</Label>
            </div>

            <Button type="submit" className="w-full" disabled={saving} data-testid="save-plan-button">
              {saving ? 'Saxlanılır...' : (editingPlan ? 'Yenilə' : 'Əlavə et')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
